/**
 * AE-CA Chat Socket Service for Account Executive
 * Handles all Socket.IO real-time communication for Account Executive ↔ Company Admin messaging
 */
import { io } from 'socket.io-client';
import { URL } from '../components/BaseUrl';

class AeCaChatSocket {
  constructor(token) {
    this.token = token;
    this.socketUrl = URL;
    this.socket = null;
    this.isConnected = false;
    this.listeners = new Map();
  }

  /**
   * Connect to Socket.IO server
   */
  connect() {
    if (this.socket && this.socket.connected) {
      console.log('🔌 Socket already connected');
      return;
    }

    console.log('🔌 Initializing socket connection...');
    this.socket = io(`${this.socketUrl}/`, {
      auth: { token: this.token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
      autoConnect: true,
      timeout: 20000,
      forceNew: true
    });

    this.setupListeners();
  }

  /**
   * Setup all socket event listeners
   */
  setupListeners() {
    if (!this.socket) return;

    // Connection status
    this.socket.on('connect', () => {
      console.log('✅ Socket connected');
      this.isConnected = true;
      this.socket.emit('ae_ca_chat_authenticate', { token: this.token });
    });

    this.socket.on('disconnect', () => {
      console.log('❌ Socket disconnected');
      this.isConnected = false;
      this.emit('disconnected', {});
    });

    this.socket.on('connect_error', (error) => {
      console.error('❌ Socket connection error:', error);
      this.isConnected = false;
      this.emit('connection_error', error);
    });

    // Authentication
    this.socket.on('ae_ca_chat_authenticated', (data) => {
      console.log('✅ AE-CA Chat authenticated:', data);
      this.emit('authenticated', data);
      if (data.user) {
        this.socket.emit('join_ae_ca_user_room', { userId: data.user.id });
        this.socket.emit('get_ae_ca_user_statuses');
      }
    });

    this.socket.on('ae_ca_chat_authentication_failed', (data) => {
      console.error('❌ AE-CA Chat authentication failed:', data);
      this.emit('authentication_failed', data);
    });

    this.socket.on('ae_ca_chat_error', (error) => {
      console.error('❌ AE-CA Chat error:', error);
      this.emit('error', error);
    });

    // User room
    this.socket.on('ae_ca_user_room_joined', (data) => {
      console.log('✅ Joined AE-CA user room:', data);
      this.emit('user_room_joined', data);
    });

    // Messages
    this.socket.on('receive_ae_ca_message', (data) => {
      console.log('📨 Received AE-CA message:', data);
      this.emit('receive_message', data);
    });

    this.socket.on('ae_ca_message_sent', (data) => {
      console.log('📤 AE-CA message sent confirmation:', data);
      this.emit('message_sent', data);
    });

    // Read receipts
    this.socket.on('ae_ca_messages_read', (data) => {
      console.log('📖 AE-CA messages marked as read:', data);
      this.emit('messages_read', data);
    });

    // Unread counts
    this.socket.on('ae_ca_thread_unread_count', (data) => {
      console.log('📊 AE-CA thread unread count:', data);
      this.emit('thread_unread_count', data);
    });

    this.socket.on('ae_ca_total_unread_count_updated', (data) => {
      console.log('📊 AE-CA total unread count updated:', data);
      this.emit('total_unread_count_updated', data);
    });

    // Typing indicators
    this.socket.on('ae_ca_user_typing', (data) => {
      console.log('⌨️ AE-CA user typing:', data);
      this.emit('user_typing', data);
    });

    // User statuses
    this.socket.on('ae_ca_user_statuses_list', (data) => {
      console.log('👥 AE-CA user statuses:', data);
      this.emit('user_statuses_list', data);
    });
  }

  /**
   * Emit: Authenticate AE-CA Chat
   */
  authenticate() {
    if (this.socket && this.isConnected) {
      this.socket.emit('ae_ca_chat_authenticate', { token: this.token });
    }
  }

  /**
   * Emit: Join AE-CA User Room
   * @param {number} userId - Account Executive user ID
   */
  joinUserRoom(userId) {
    if (this.socket && this.isConnected) {
      this.socket.emit('join_ae_ca_user_room', { userId });
    }
  }

  /**
   * Emit: Join AE-CA Chat Room
   * @param {number} threadId - Thread ID
   */
  joinChatRoom(threadId) {
    if (this.socket && this.isConnected) {
      this.socket.emit('join_ae_ca_chat_room', { threadId });
    }
  }

  /**
   * Emit: Send AE-CA Message
   * @param {number} threadId - Thread ID
   * @param {string} content - Message content
   * @param {string} messageType - Message type: 'text', 'image', 'file', 'system'
   * @param {string|null} fileUrl - File URL (optional)
   * @param {string|null} fileName - File name (optional)
   * @param {number|null} fileSize - File size in bytes (optional)
   * @param {string|null} mimeType - MIME type (optional)
   * @param {string|null} tempId - Temporary ID for optimistic UI (optional)
   */
  sendMessage(threadId, content, messageType = 'text', fileUrl = null, fileName = null, fileSize = null, mimeType = null, tempId = null) {
    if (this.socket && this.isConnected) {
      this.socket.emit('send_ae_ca_message', {
        threadId,
        content,
        messageType,
        fileUrl,
        fileName,
        fileSize,
        mimeType,
        tempId
      });
    }
  }

  /**
   * Emit: Send AE-CA Image (Base64)
   * @param {number} threadId - Thread ID
   * @param {number} receiverId - Receiver user ID
   * @param {string} base64Data - Base64 encoded image data
   * @param {string} fileName - File name
   * @param {string} mimeType - MIME type
   * @param {string|null} tempId - Temporary ID for optimistic UI (optional)
   */
  sendImageBase64(threadId, receiverId, base64Data, fileName, mimeType, tempId = null) {
    if (this.socket && this.isConnected) {
      this.socket.emit('send_ae_ca_image_base64', {
        threadId,
        receiverId,
        base64Data,
        fileName,
        mimeType,
        tempId
      });
    }
  }

  /**
   * Emit: Mark AE-CA Messages as Read
   * @param {number} threadId - Thread ID
   * @param {number|null} upToMessageId - Optional message ID to mark up to
   */
  markAsRead(threadId, upToMessageId = null) {
    if (this.socket && this.isConnected) {
      this.socket.emit('mark_ae_ca_read', {
        threadId,
        upToMessageId
      });
    }
  }

  /**
   * Emit: Mark Specific AE-CA Message as Read
   * @param {number} messageId - Message ID
   * @param {number} threadId - Thread ID
   */
  markMessageAsRead(messageId, threadId) {
    if (this.socket && this.isConnected) {
      this.socket.emit('mark_ae_ca_message_read', {
        messageId,
        threadId
      });
    }
  }

  /**
   * Emit: Mark AE-CA Message as Delivered
   * @param {number} messageId - Message ID
   * @param {number} threadId - Thread ID
   */
  markMessageAsDelivered(messageId, threadId) {
    if (this.socket && this.isConnected) {
      this.socket.emit('mark_ae_ca_message_delivered', {
        messageId,
        threadId
      });
    }
  }

  /**
   * Emit: Get AE-CA Thread Unread Count
   * @param {number} threadId - Thread ID
   */
  getThreadUnreadCount(threadId) {
    if (this.socket && this.isConnected) {
      this.socket.emit('get_ae_ca_thread_unread_count', { threadId });
    }
  }

  /**
   * Emit: Get AE-CA Total Unread Count
   */
  getTotalUnreadCount() {
    if (this.socket && this.isConnected) {
      this.socket.emit('get_ae_ca_total_unread_count');
    }
  }

  /**
   * Emit: Start Typing Indicator
   * @param {number} threadId - Thread ID
   */
  startTyping(threadId) {
    if (this.socket && this.isConnected) {
      this.socket.emit('ae_ca_typing_start', { threadId });
    }
  }

  /**
   * Emit: Stop Typing Indicator
   * @param {number} threadId - Thread ID
   */
  stopTyping(threadId) {
    if (this.socket && this.isConnected) {
      this.socket.emit('ae_ca_typing_stop', { threadId });
    }
  }

  /**
   * Emit: Get AE-CA User Statuses
   */
  getUserStatuses() {
    if (this.socket && this.isConnected) {
      this.socket.emit('get_ae_ca_user_statuses');
    }
  }

  /**
   * Register event listener
   * @param {string} event - Event name
   * @param {Function} callback - Callback function
   */
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
  }

  /**
   * Remove event listener
   * @param {string} event - Event name
   * @param {Function} callback - Callback function to remove
   */
  off(event, callback) {
    if (this.listeners.has(event)) {
      const callbacks = this.listeners.get(event);
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  /**
   * Emit custom event to registered listeners
   * @param {string} event - Event name
   * @param {*} data - Event data
   */
  emit(event, data) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach(callback => callback(data));
    }
  }

  /**
   * Disconnect from Socket.IO server
   */
  disconnect() {
    if (this.socket) {
      console.log('🔌 Disconnecting socket...');
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      this.listeners.clear();
    }
  }

  /**
   * Get connection status
   * @returns {boolean} Connection status
   */
  getConnectionStatus() {
    return this.isConnected && this.socket && this.socket.connected;
  }
}

export default AeCaChatSocket;

